import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BellRing, Download } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { mensagemDeErro, post } from '../../nucleo/api';
import { data, nomePessoa } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { textoSensivel } from '../../componentes/sensivel';
import { rotuloTipoDocumento, SeloSituacao } from './FichaPartes';
import type { DocumentoItem } from './tipos';

/** M7.3: documentos de todos os funcionários, com validade e alerta de vencimento. */
export function Documentos() {
  const { t } = useTranslation('pessoas');
  const s = useSessao();
  const toast = useToast();
  const navegar = useNavigate();
  const [situacao, setSituacao] = useState('alerta');

  const verificar = useMutation({
    mutationFn: () => post<{ job_id: string }>('/pessoas/documentos/verificar-vencimentos', {}),
    onSuccess: () => toast.sucesso(t('documentos.verificacaoIniciada')),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  const colunas: Coluna<DocumentoItem>[] = [
    { chave: 'funcionario_nome', rotulo: t('campos.funcionario'), principal: true, render: (d) => <span className="font-medium">{nomePessoa(d.funcionario_nome)}</span> },
    { chave: 'tipo', rotulo: t('campos.tipoDocumento'), render: (d) => rotuloTipoDocumento(d.tipo, t) },
    { chave: 'numero', rotulo: t('campos.numero'), mono: true, render: (d) => textoSensivel(d.numero) },
    { chave: 'validade', rotulo: t('campos.validade'), mono: true, render: (d) => (d.validade ? data(d.validade) : '') },
    { chave: 'situacao', rotulo: t('campos.situacao'), render: (d) => <SeloSituacao d={d} /> },
    {
      chave: 'anexo',
      rotulo: t('campos.anexo'),
      render: (d) =>
        d.anexo_id ? (
          <a href={`/api/v1/anexos/${d.anexo_id}`} target="_blank" rel="noreferrer" aria-label={t('acoes.baixarAnexo')} onClick={(e) => e.stopPropagation()} className="inline-flex text-tinta-3 hover:text-tinta-1">
            <Download className="size-4" />
          </a>
        ) : (
          ''
        ),
    },
  ];

  return (
    <>
      {s.pode('pessoas.editar') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<BellRing className="size-4" />} carregando={verificar.isPending} onClick={() => verificar.mutate()}>
            {t('acoes.verificarVencimentos')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Tabela<DocumentoItem>
        tela="pessoas.documentos"
        caminho="/pessoas/documentos"
        params={{ situacao: situacao || undefined }}
        filtros={{ situacao }}
        aoAplicarFiltros={(f) => setSituacao(String(f.situacao))}
        colunas={colunas}
        aoClicar={(d) => navegar(`/pessoas/pessoas/funcionario/${d.funcionario_id}`)}
        vazio={<Vazio texto={situacao === 'alerta' ? t('vazio.documentosAlerta') : t('vazio.documentos')} />}
        filtrosExtra={
          <div className="w-full sm:w-56">
            <Selecao
              aria-label={t('campos.situacao')}
              vazio={t('filtros.todosDocumentos')}
              opcoes={[
                { valor: 'alerta', rotulo: t('filtros.vencidosEVencendo') },
                { valor: 'vencido', rotulo: t('situacao.vencido') },
                { valor: 'vencendo', rotulo: t('situacao.vencendo') },
                { valor: 'valido', rotulo: t('situacao.valido') },
                { valor: 'sem_validade', rotulo: t('situacao.sem_validade') },
              ]}
              value={situacao}
              onChange={(e) => setSituacao(e.target.value)}
            />
          </div>
        }
      />
    </>
  );
}
